import { LaborRateMode, LaborRateProfile } from '@/app/types/labor-rate';
import type { BlueprintPageBudget } from '@/app/types/uploads';
import { ApiClient, ApiClientProps } from './client';
import { LaborDifficulty } from './organizations';

type OnboardProps = {
  first_name: string;
  last_name: string;
  organization_name: string;
  organization_size: string;
  estimated_takeoffs_per_month: number;
  referral_source: string;
};

type CurrentUserUpdateProps = {
  first_name?: string;
  last_name?: string;
  organization_name?: string;
};

type ChangePasswordProps = {
  new_password: string;
  re_new_password: string;
  current_password: string;
};

export type Team = {
  id: number;
  name: string;
  organization_name: string;
};

export type CurrentUser = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  organization: {
    id: number;
    name: string;
    labor_base_rate: string;
    default_labor_rate_mode: LaborRateMode;
    default_labor_rate_profile: LaborRateProfile;
    use_my_pricing: boolean;
    show_issue_estimated_impact: boolean;
    multiple_design_stages_enabled: boolean;
    project_creator_enabled?: boolean;
    labor_difficulty: LaborDifficulty;
    custom_labor_modifier: string;
    material_markup_percent: string;
    labor_markup_percent: string;
    general_admin_expense_rate: string;
    sales_tax_rate: string;
    risk_insurance_rate: string;
    profit_margin_rate: string;
    free_project_limit: number;
    project_count: number;
    has_active_subscription: boolean;
    features?: string[];
  };
  role: string;
  is_superadmin: boolean;
  active_team_id: number | null;
  can_manage_billing: boolean;
  intercom_jwt: string | null;
  features?: string[];
};

type CurrentUserBilling = {
  plan?: string;
  price?: number; // Price in cents
  interval?: string; // "month" or "year"
  next_payment_date?: string;
  payment_method?: {
    brand: string;
    last4: string;
  };
};

type CurrentUserBillingPortal = {
  url: string;
};

export type StripePlan = {
  id: number;
  name: string;
};

export type PendingInvite = {
  id: number;
  organization_name: string;
  role: string;
  token: string;
  created_at: string;
  expires_at: string;
};

type CurrentUserOnboarding = {
  success: boolean;
};

/**
 * API client for projects-related endpoints
 */
export class CurrentUserClient extends ApiClient {
  constructor(props: ApiClientProps) {
    super(props);
  }

  fetch = async () => {
    return this._get<CurrentUser>('users/me/');
  };

  update = async (data: CurrentUserUpdateProps) => {
    return this._patch<CurrentUser>('users/me/', data);
  };

  onboard = async (data: OnboardProps) => {
    return this._post<CurrentUserOnboarding>('users/me/onboarding/', data);
  };

  changePassword = async (data: ChangePasswordProps) => {
    return this._post('auth/users/set_password/', data, undefined, '');
  };

  fetchBilling = async () => {
    return this._get<CurrentUserBilling>('users/me/billing/');
  };

  fetchPlans = async () => {
    return this._get<StripePlan[]>('users/me/billing/plans/');
  };

  billingPortal = async (returnUrl: string, planId?: number) => {
    return this._post<CurrentUserBillingPortal>('users/me/billing/portal/', {
      return_url: returnUrl,
      ...(planId && { plan_id: planId }),
    });
  };

  setActiveTeam = async (teamId: number) => {
    return this._patch<CurrentUser>('users/me/active-team', { team_id: teamId });
  };

  fetchTeams = async () => {
    return this._get<Team[]>('users/me/teams');
  };

  fetchMyInvites = async () => {
    return this._get<PendingInvite[]>('users/me/invites/');
  };

  preflightBlueprintPages = async (selectedPages: number, projectId?: number) => {
    return this._post<BlueprintPageBudget>('users/me/blueprint-page-preflight/', {
      selected_pages: selectedPages,
      ...(projectId === undefined ? {} : { project_id: projectId }),
    });
  };
}
